import React, { useEffect, useState, useCallback } from 'react';
import { Box, CircularProgress, Typography, Avatar, IconButton, Tooltip } from '@mui/material';
import { LogoutOutlined } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../hooks/useApi';
import LobbyBuilderTab from './events/lobbyBuilderTab/LobbyBuilderTab';

const PRIMARY = '#1075BA';

/**
 * LobbyAdminView
 * --------------
 * The entire UI for users with isLobbyAdmin === true.
 * No dashboard, no tab bar, no EventEditor chrome — just the lobby builder
 * (or chat-only view depending on lobbyAdminMode) for their assigned lobby.
 */
export default function LobbyAdminView() {
  const { user, lobbyAdminMode, logout } = useAuth();

  const [event,   setEvent]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  const eventId = user?.allowedEvents?.[0]?._id
    || user?.allowedEvents?.[0]
    || null;

  const hue = ((user?.firstName?.charCodeAt(0) || 65) * 13) % 360;

  const load = useCallback(async () => {
    if (!eventId) {
      setError('No event assigned to your account. Please contact your administrator.');
      setLoading(false);
      return;
    }
    try {
      const r = await api.get(`/events/${eventId}`);
      setEvent(r.data.data);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to load your assigned event.');
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(145deg, #edf3fb 0%, #f5f7fb 50%, #edf6f3 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <CircularProgress sx={{ color: PRIMARY }} />
    </Box>
  );

  if (error) return (
    <Box sx={{
      minHeight: '100vh', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 2, p: 4,
    }}>
      <Typography sx={{ fontWeight: 700, color: 'text.secondary', textAlign: 'center' }}>
        {error}
      </Typography>
      <Box
        onClick={logout}
        sx={{
          px: 2.5, py: 1, borderRadius: 2, border: '1px solid #e4e9f0',
          fontSize: '0.85rem', fontWeight: 600, color: '#5a6474',
          cursor: 'pointer', '&:hover': { background: '#f5f7fa' },
        }}
      >
        Sign out
      </Box>
    </Box>
  );

  if (!event) return null;

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#f0f4f8' }}>

      {/* ── Header bar ─────────────────────────────────────────────────────── */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 2.5, height: 56, flexShrink: 0,
        background: '#fff',
        borderBottom: '1px solid #e8edf2',
        boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
      }}>

        {/* Left — GV logo mark + event name */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
          <Box sx={{
            width: 30, height: 30, borderRadius: '50%',
            background: PRIMARY, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Box component="span" sx={{ color: '#fff', fontSize: '0.7rem', fontWeight: 800, lineHeight: 1 }}>
              GV
            </Box>
          </Box>
          <Box sx={{ width: '1px', height: 20, background: '#e8edf2', flexShrink: 0 }} />
          <Typography sx={{
            fontSize: '0.88rem', fontWeight: 700, color: '#1a1a2e',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            maxWidth: { xs: 160, sm: 300, md: 480 },
          }}>
            {event.name}
          </Typography>
        </Box>

        {/* Right — avatar + name + role + logout */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0 }}>
          <Avatar sx={{
            width: 32, height: 32,
            bgcolor: `hsl(${hue},50%,88%)`,
            color:   `hsl(${hue},40%,30%)`,
            fontSize: '0.75rem', fontWeight: 700,
          }}>
            {user?.firstName?.[0]}{user?.lastName?.[0]}
          </Avatar>
          <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1a1a2e', lineHeight: 1.2 }}>
              {user?.firstName} {user?.lastName}
            </Typography>
            <Typography sx={{ fontSize: '0.68rem', color: '#9ea8b3', lineHeight: 1.3 }}>
              {user?.role?.name || 'Lobby Admin'}
            </Typography>
          </Box>
          <Tooltip title="Sign out">
            <IconButton
              onClick={logout}
              size="small"
              sx={{ color: '#b0bac4', ml: 0.5, '&:hover': { color: '#d32f2f', background: alpha('#d32f2f', 0.06) } }}
            >
              <LogoutOutlined sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* ── Lobby builder (takes remaining height) ──────────────────────────── */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <LobbyBuilderTab event={event} isLobbyAdminView />
      </Box>
    </Box>
  );
}